/** Verified target window: legacy input-minus-100ms, or the explicit authored frame's actual time. */
import { z } from 'zod';

const time = z.number().int().nonnegative().safe();
export const calibrationEvidenceReferenceWindowSchema = z.object({ referenceTargetUs: time,
  frameIntervalUs: time.refine(value => value > 0), jitterBoundUs: time,
}).strict().refine(window => Number.isSafeInteger(window.frameIntervalUs + window.jitterBoundUs),
  'Reference cadence bounds must fit a safe integer');
export type CalibrationEvidenceReferenceWindow = z.infer<typeof calibrationEvidenceReferenceWindowSchema>;

export function validateCalibrationEvidenceReferenceWindow(report: {
  referenceWindow?: CalibrationEvidenceReferenceWindow; targetId?: string;
  frames?: ReadonlyArray<{ id?: string; tUs?: number; offsetUs?: number }>; omittedOffsetsUs?: readonly number[];
}, context: z.RefinementCtx): void {
  const radius = 100_000, window = report.referenceWindow;
  const fail = (message: string) => context.addIssue({ code: z.ZodIssueCode.custom, path: ['referenceWindow'], message });
  const frames = report.frames, omitted = report.omittedOffsetsUs;
  if (!frames || !omitted || frames.some(frame => typeof frame.tUs !== 'number' || typeof frame.offsetUs !== 'number')) {
    fail('Reference frame clocks and omissions are required'); return;
  }
  if (!window) {
    if ([...frames.map(frame => frame.offsetUs ?? NaN), ...omitted].some(offset => Math.abs(offset) > radius)) {
      fail('Legacy evidence offsets must remain within the inclusive actual-anchor window');
    }
    return;
  }
  const target = frames.find(frame => frame.id === report.targetId);
  if (typeof window.referenceTargetUs !== 'number' || typeof window.frameIntervalUs !== 'number'
    || typeof window.jitterBoundUs !== 'number' || typeof target?.tUs !== 'number') {
    fail('Verified reference window and its actual anchor are required'); return;
  }
  const bound = window.frameIntervalUs + window.jitterBoundUs;
  const lower = Math.max(0, window.referenceTargetUs - radius), upper = window.referenceTargetUs + radius;
  if (!Number.isSafeInteger(upper) || !target || target.tUs > window.referenceTargetUs
    || window.referenceTargetUs - target.tUs > bound) {
    fail('Authoring anchor must be the bounded actual predecessor of its verified reference target');
    return;
  }
  const times = frames.map(frame => frame.tUs ?? NaN);
  const first = Math.min(...times), last = Math.max(...times);
  if (times.some(timeUs => timeUs < Math.max(0, lower - bound) || timeUs > upper)
    || times.filter(timeUs => timeUs < lower).length > 1 || first > lower || lower - first > bound
    || upper - last > bound) fail('Authoring evidence must retain the full bounded target neighborhood and its lower predecessor');
  const anchorUs = target.tUs;
  if (omitted.some(offset => !Number.isSafeInteger(anchorUs + offset)
    || anchorUs + offset < Math.max(0, lower - bound) || anchorUs + offset > upper)) {
    fail('Omitted samples must belong to the same verified target neighborhood');
  }
}
